import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  // Determine context from URL path
  const isFreelancer = window.location.pathname.includes('/freelancer/');
  const loginPath = isFreelancer ? '/login?type=freelancer' : '/login';
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request', 'sent', 'reset', 'success'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setTokenValid(true);
        setEmail(data.email);
      } else {
        setTokenValid(false);
        setError('Der Reset-Link ist ungültig oder abgelaufen.');
      }
    } catch (err) {
      setTokenValid(false);
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setStep('sent');
      } else {
        setError('Ein Fehler ist aufgetreten. Bitte versuche es später erneut.');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      if (response.ok) {
        setStep('success');
      } else {
        const data = await response.json();
        setError(data.detail || 'Ein Fehler ist aufgetreten.');
      }
    } catch (err) {
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Logo - zentriert */}
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center">
          <img src="/revocut-logo.png" alt="RevoCut" className="w-9 h-9" />
          <span className="text-2xl font-bold text-white ml-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            REVOCUT
          </span>
        </Link>
      </div>

      <div className="w-full max-w-md">
        {/* Request Reset Form */}
        {step === 'request' && (
          <div className="p-8 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Passwort vergessen?
            </h1>
            <p className="text-gray-400 mb-6">
              Gib deine E-Mail-Adresse ein und wir senden dir einen Link zum Zurücksetzen.
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="deine@email.de"
                  data-testid="reset-email-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-submit-btn"
                className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#5C63FF' }}
              >
                {loading ? 'Wird gesendet...' : 'Reset-Link senden'}
              </button>
            </form>

            <Link 
              to={loginPath}
              className="mt-6 flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zum Login
            </Link>
          </div>
        )}

        {/* Email Sent Confirmation */}
        {step === 'sent' && (
          <div className="p-8 rounded-2xl border border-white/10 text-center" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
              <Mail className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              E-Mail gesendet!
            </h1>
            <p className="text-gray-400 mb-6">
              Falls ein Account mit <strong className="text-white">{email}</strong> existiert, 
              haben wir dir einen Link zum Zurücksetzen gesendet.
            </p>
            <p className="text-gray-500 text-sm mb-6">
              Prüfe auch deinen Spam-Ordner.
            </p>

            <Link 
              to={loginPath}
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zum Login
            </Link>
          </div>
        )}

        {/* Reset Password Form */}
        {step === 'reset' && (
          <div className="p-8 rounded-2xl border border-white/10" style={{ backgroundColor: '#1a1a1a' }}>
            {tokenValid === null ? (
              <div className="text-center">
                <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-gray-400 mt-4">Link wird überprüft...</p>
              </div>
            ) : tokenValid === false ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}>
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Link ungültig</h1>
                <p className="text-gray-400 mb-6">{error}</p>
                <button
                  onClick={() => { setStep('request'); setError(''); }}
                  className="px-6 py-3 rounded-xl font-semibold text-white"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  Neuen Link anfordern
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Neues Passwort setzen
                </h1>
                <p className="text-gray-400 mb-6">
                  Für: <strong className="text-white">{email}</strong>
                </p>

                {error && (
                  <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full pl-12 pr-12 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder="Neues Passwort"
                      data-testid="new-password-input"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full pl-12 pr-12 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder="Passwort bestätigen"
                      data-testid="confirm-password-input"
                    />
                    <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="set-password-btn"
                    className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: '#5C63FF' }}
                  >
                    {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="p-8 rounded-2xl border border-white/10 text-center" style={{ backgroundColor: '#1a1a1a' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Passwort geändert!
            </h1>
            <p className="text-gray-400 mb-6">
              Dein Passwort wurde erfolgreich aktualisiert.
            </p>

            <Link 
              to={loginPath}
              className="inline-block w-full py-4 rounded-xl font-semibold text-white text-center"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Zum Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
